import * as React from 'react';

import { FileItem, UploadOptions } from './types';
import { uploadAttachmentFunction } from '../attachment-node/utils';

function useFileUploadTipTap(options: UploadOptions): {
  fileItem: FileItem | null;
  uploadFiles: (files: File[]) => Promise<string | null>;
  clearFileItem: () => void;
} {
  const [fileItem, setFileItem] = React.useState<FileItem | null>(null);

  const uploadFile = async (file: File): Promise<string | null> => {
    if (file.size > options.maxSize) {
      const error = new Error(`File size exceeds maximum allowed (${options.maxSize / 1024 / 1024}MB)`);
      options.onError?.(error);
      return null;
    }

    const abortController = new AbortController();

    try {
      // For images, use the original upload function
      if (file.type.startsWith('image/')) {
        const newFileItem: FileItem = {
          id: crypto.randomUUID(),
          file,
          progress: 0,
          status: 'uploading',
          abortController,
        };

        setFileItem(newFileItem);
        if (!options.upload) {
          throw new Error('Upload function is not defined');
        }

        const url = await options.upload(
          file,
          (event: { progress: number }) => {
            setFileItem((prev) => {
              if (!prev) return null;
              return {
                ...prev,
                progress: event.progress,
              };
            });
          },
          abortController.signal
        );

        if (!url) throw new Error('Upload failed: No URL returned');

        if (!abortController.signal.aborted) {
          setFileItem((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              status: 'success',
              url,
              progress: 100,
            };
          });
          options.onSuccess?.(url);
          return url;
        }
      } else {
        uploadAttachmentFunction(
          options.editor,
          [file],
          options.pos,
          options.entityId || '',
          options.enqueueErrorSnackbar || (() => {})
        );
      }

      return null;
    } catch (error) {
      if (!abortController.signal.aborted) {
        setFileItem((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            status: 'error',
            progress: 0,
          };
        });
        options.onError?.(error instanceof Error ? error : new Error('Upload failed'));
      }
      return null;
    }
  };

  const uploadFiles = async (files: File[]): Promise<string | null> => {
    if (!files || files.length === 0) {
      options.onError?.(new Error('No files to upload'));
      return null;
    }

    if (options.limit && files.length > options.limit) {
      options.onError?.(new Error(`Maximum ${options.limit} file${options.limit === 1 ? '' : 's'} allowed`));
      return null;
    }

    const file = files[0];
    if (!file) {
      options.onError?.(new Error('File is undefined'));
      return null;
    }

    return uploadFile(file);
  };

  const clearFileItem = (): void => {
    if (!fileItem) return;

    if (fileItem.abortController) {
      fileItem.abortController.abort();
    }
    if (fileItem.url) {
      URL.revokeObjectURL(fileItem.url);
    }
    setFileItem(null);
  };

  return {
    fileItem,
    uploadFiles,
    clearFileItem,
  };
}

export default useFileUploadTipTap;
